/*
  Side drawers — snippets/drawer.liquid
  -------------------------------------
  Deliberately thin. `showModal()` already gives us the focus trap, Esc-to-close, the
  inert background and the top layer, and `assets/critical.css` keys the page scroll
  lock off the `scroll-lock` attribute. What is left is the three things the platform
  does not do:

    • Opening from anywhere. `[data-drawer-open="<id>"]` on any control.
    • Returning focus to whatever opened it, on every exit path. Esc, the close
      button and a backdrop click all fire `close`, so the return target is parked on
      the dialog rather than tracked per exit.
    • Closing on a backdrop click. A click on the scrim targets the dialog element
      itself, but only outside the panel — so the panel's own box is what tells the
      two apart, not event bubbling.

  Delegated from `document`, so a drawer rendered into the page later needs no
  initialisation.
*/
(function () {
  if (window.__drawer) return;
  window.__drawer = true;

  /* Something outside the theme has been observed writing `position: relative` onto
     the open dialog's inline style, which unpins it from the viewport and collapses
     it into the section grid. This observer is the whole defence now (no !important
     in the CSS): it strips the injected property as it lands and says so in the
     console, so the culprit can be caught red-handed with a "break on attribute
     change" if wanted. */
  function guardStyle(dialog) {
    if (dialog.__drawerGuard) return;
    dialog.__drawerGuard = new MutationObserver(function () {
      if (dialog.style.position || dialog.style.height || dialog.style.width) {
        console.warn('[drawer] stripped injected inline style:', dialog.getAttribute('style'));
        dialog.style.removeProperty('position');
        dialog.style.removeProperty('height');
        dialog.style.removeProperty('width');
      }
    });
    dialog.__drawerGuard.observe(dialog, { attributes: true, attributeFilter: ['style'] });
  }

  function open(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;
    guardStyle(dialog);
    dialog.__drawerReturnFocus = trigger || null;
    dialog.showModal();

    // Land on the first thing worth acting on rather than on the close button, which
    // is where the focus trap would otherwise start.
    var first = dialog.querySelector('[data-drawer-autofocus]');
    if (first) first.focus({ preventScroll: true });

    dialog.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  /* The exit is entirely CSS-driven. close() removes [open]; the transform target
     returns to the side's offset and the transition list's allow-discrete `display`
     and `overlay` hold the dialog rendered, in the top layer, until the slide has
     finished. No is-closing class, no transitionend juggling: the class was lifted
     one frame before close(), and for that frame the still-open dialog animated back
     toward translateX(0) -- the visible close-reopen-vanish. One state change cannot
     get its own order wrong. */
  function close(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      open(document.getElementById(opener.getAttribute('data-drawer-open')), opener);
      return;
    }

    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      close(closer.closest('[data-drawer]'));
      return;
    }

    // Backdrop. A click on ::backdrop is dispatched with the dialog itself as the
    // target, and the dialog is the card now — so a target of the dialog with
    // coordinates outside its own box can only be the scrim. (Clicks inside land on
    // the header/body/footer, never on the dialog itself.)
    var dialog = e.target.closest ? e.target.closest('[data-drawer]') : null;
    if (dialog && e.target === dialog) {
      var box = dialog.getBoundingClientRect();
      var outside =
        e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom;
      if (outside) close(dialog);
    }
  });

  /* No `cancel` interception any more: it existed to reroute Esc into the scripted
     exit, and the exit is CSS now. The UA's own close() takes the same animated
     path as every other route out. */

  // `close` does not bubble — listen in the capture phase.
  document.addEventListener(
    'close',
    function (e) {
      var dialog = e.target;
      if (!dialog || !dialog.matches || !dialog.matches('[data-drawer]')) return;
      var target = dialog.__drawerReturnFocus;
      dialog.__drawerReturnFocus = null;
      if (target && document.contains(target)) target.focus({ preventScroll: true });
      dialog.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    },
    true
  );

  /* The scrollbar inside a drawer is invisible at rest and appears while the panel is
     actually being scrolled. CSS has :hover and :focus-within but no "is scrolling",
     so that one state is set here and cleared once the scroll settles. */
  document.addEventListener(
    'scroll',
    function (e) {
      var body = e.target;
      if (!body || !body.classList || !body.hasAttribute || !body.hasAttribute('data-drawer-body')) return;
      body.classList.add('is-scrolling');
      clearTimeout(body.__drawerScrollTimer);
      body.__drawerScrollTimer = setTimeout(function () {
        body.classList.remove('is-scrolling');
      }, 700);
    },
    true
  );

  // Guard every drawer up front, not only the ones opened through open() — the quick
  // add sheet calls showModal() itself and would otherwise open unguarded.
  function ready() {
    var dialogs = document.querySelectorAll('[data-drawer]');
    for (var i = 0; i < dialogs.length; i++) guardStyle(dialogs[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  // Theme editor lifecycle. Drop the return target first: the drawer is still
  // attached here, so the close handler would otherwise focus a control that is
  // about to be removed.
  document.addEventListener('shopify:section:unload', function (e) {
    var drawers = e.target.querySelectorAll('[data-drawer]');
    for (var i = 0; i < drawers.length; i++) {
      drawers[i].__drawerReturnFocus = null;
      close(drawers[i]);
    }
  });
})();
